// File: src/pages/teams/detailCards/emailBroadcasts.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../../firebase-client';

/* ---- date helpers (local, not UTC) ---- */
function toInputDateLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fromInputDateLocal(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1); // local midnight
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function pct(n, d) {
  if (!d || d <= 0) return '—';
  return `${Math.min(100, Math.round(n / d * 100))}%`;
}

function formatSentAt(value) {
  const date = value?.toDate?.() || value || null;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function Metric({ label, value, detail }) {
  return (
    <span className="email-broadcast-metric" title={label}>
      <span className="email-broadcast-metric__label">{label}</span>
      <span className="email-broadcast-metric__value">
        <strong>{value}</strong>
        {detail ? <span>{detail}</span> : null}
      </span>
    </span>
  );
}

export default function EmailBroadcastsCard({
  teamId,
  isAdmin,
  onCompose,
  onOpenCampaign
}) {
  const [sentFrom, setSentFrom] = useState(toInputDateLocal(new Date(Date.now() - 7 * 86400000)));
  const [sentTo, setSentTo] = useState(toInputDateLocal(new Date()));
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  // Keep the dashboard card compact; older campaigns remain available through paging.
  const PAGE_SIZE = 2;
  const [page, setPage] = useState(0);
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!teamId) return;
      setLoading(true);
      try {
        const colRef = collection(db, 'teams', teamId, 'emailCampaigns');
        const qRef = query(colRef, orderBy('sentAt', 'desc'));
        const snap = await getDocs(qRef);
        const start = fromInputDateLocal(sentFrom);
        const end = fromInputDateLocal(sentTo);
        const endPlus1 = end ? new Date(end.getTime() + 86400000) : null; // inclusive

        const rows = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
        })).filter(c => {
          if (!c?.status || !['sent', 'sending'].includes(c.status)) return false;
          const s = c.sentAt?.toDate?.() || c.sentAt || null;
          if (!s) return false;
          if (start && s < start) return false;
          if (endPlus1 && s >= endPlus1) return false;
          return true;
        });
        if (isMounted) {
          setCampaigns(rows);
          setPage(0); // reset to first page when filters/team change
        }
      } catch (e) {
        if (isMounted) setCampaigns([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [teamId, sentFrom, sentTo]);
  const summary = useMemo(() => {
    const total = campaigns.length;
    const delivered = campaigns.reduce((a, c) => a + (c?.totals?.delivered || 0), 0);
    const openedUnique = campaigns.reduce((a, c) => a + (c?.totals?.openedUnique || 0), 0);
    const replies = campaigns.reduce((a, c) => a + (c?.totals?.inboundReplies || c?.totals?.replies || 0), 0);
    return {
      total,
      delivered,
      openedUnique,
      replies
    };
  }, [campaigns]);
  const totalPages = Math.max(1, Math.ceil(campaigns.length / PAGE_SIZE));
  const startIdx = page * PAGE_SIZE;
  const visible = campaigns.slice(startIdx, startIdx + PAGE_SIZE);
  return <div className="team-card email-broadcasts-card">
      <div className="card-header-with-icon">
        <span className="card-title">Emails</span>
      </div>

      <div className="card-body email-broadcast-card">
        <div className="email-broadcast-toolbar">
          <div className="email-broadcast-filters">
            <label className="email-broadcast-filter">
              <span className="form-label">
                From
              </span>
              <input type="date" value={sentFrom} onChange={e => setSentFrom(e.target.value)} />
            </label>

            <label className="email-broadcast-filter">
              <span className="form-label">
                To
              </span>
              <input type="date" value={sentTo} onChange={e => setSentTo(e.target.value)} />
            </label>
          </div>

          {isAdmin && (
              <button className="btn btn--primary email-broadcast-new-button" onClick={() => onCompose?.()} aria-label="Start a new email">
                New Email
              </button>
          )}
        </div>

        <div className="email-broadcast-summary" aria-live="polite">
          {loading ? (
            <span className="text-muted">Loading...</span>
          ) : (
            <>
              <Metric label="Emails" value={summary.total} />
              <Metric label="Delivered" value={summary.delivered} />
              <Metric label="Open rate" value={pct(summary.openedUnique, summary.delivered)} />
              <Metric label="Replies" value={summary.replies} />
            </>
          )}
        </div>

        {campaigns.length > PAGE_SIZE && <div className="email-broadcast-pager">
            <button className="btn btn--ghost" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous emails" title="Previous">
              ‹
            </button>
            <span className="text-muted">
              {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, campaigns.length)} of {campaigns.length}
            </span>
            <button className="btn btn--ghost" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} aria-label="Next emails" title="Next">
              ›
            </button>
          </div>}

        {visible.length === 0 && !loading && <p className="text-muted email-broadcast-empty">
            No emails in this range yet.
          </p>}

        <div className="email-broadcast-list">
          {visible.map(c => {
          const delivered = c?.totals?.delivered || 0;
          const openedUnique = c?.totals?.openedUnique || 0;
          const clickedUnique = c?.totals?.clickedUnique || 0;
          const bounced = c?.totals?.bounced || 0;
          const replies = c?.totals?.inboundReplies || c?.totals?.replies || 0;
          return <button key={c.id} className="email-broadcast-row" onClick={() => onOpenCampaign?.(c)} aria-label={`Open email ${c?.name || c?.subject || 'campaign'}`}>
                <div className="email-broadcast-row__main">
                  <div className="email-broadcast-row__title">
                    {c?.name || c?.subject || '(no subject)'}
                  </div>
                  <div className="email-broadcast-row__meta">
                    {formatSentAt(c?.sentAt)} · {c?.status || 'unknown'}
                  </div>
                </div>

                <div className="email-broadcast-row__metrics" aria-label="Email metrics">
                  <span><strong>{delivered}</strong> delivered</span>
                  <span><strong>{openedUnique}</strong> opened ({pct(openedUnique, delivered)})</span>
                  <span><strong>{clickedUnique}</strong> clicked</span>
                  <span><strong>{bounced}</strong> bounced</span>
                  <span><strong>{replies}</strong> replies</span>
                </div>
              </button>;
        })}
        </div>

        <p className="text-muted email-broadcast-note">
          Note: Open rates can be inflated by privacy protections (e.g., Apple MPP). Treat them as directional.
        </p>
      </div>
    </div>;
}
